import { useState } from 'react';
import { useMutation } from 'react-query';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import {format} from 'date-fns';
import { useCCTVVideoRequests, useDownloadCCTVVideosArchive } from '../../services/requests';
import { DEFAULT_ITEMS_PER_PAGE } from '../../constants';
import { QueryParams } from '../../types/general.types';
import { CCTVVideoRequest } from '../../types/cctv.types';
import {
  toIsoStringInUTC,
  getErrorMessage,
  downloadZip,
  getLastWeeksDate,
  getYesterdayDate,
  roundMinutesToNearest30,
} from '../../utils';

export const useCCTV = () => {
  const { t } = useTranslation();
  const [queryParams, setQueryParams] = useState<QueryParams>({
    pageNumber: 1,
    pageSize: DEFAULT_ITEMS_PER_PAGE,
    sort: 'desc',
    orderBy: 'createdAt',
  });
  const [startDate, setStartDate] = useState<Date | null>(() => {
    const date = getLastWeeksDate();
    if (date) {
      date.setHours(0, 0, 0, 0); // Set to 12:00 AM
    }
    return date;
  });
  const [endDate, setEndDate] = useState<Date | null>(
    roundMinutesToNearest30(new Date())
  );

  const { data, isLoading, error } = useCCTVVideoRequests(queryParams);

  const downloadVideosMutation = useMutation(useDownloadCCTVVideosArchive);

  const setQuerySort = (sort: string) => {
    setQueryParams((prevState) => ({ ...prevState, sort }));
  };

  const setQueryOrderBy = (orderBy: string) => {
    setQueryParams((prevState) => ({ ...prevState, orderBy }));
  };

  const handleSearch = () => {
    setQueryParams((prev) => ({
      ...prev,
      startDate: startDate ? toIsoStringInUTC(startDate) : '',
      endDate: endDate ? toIsoStringInUTC(endDate) : '',
      pageNumber: 1,
    }));
  };

  const refreshCctvVideoRequestsTable = () => {
    setQueryParams((prev) => ({
      ...prev,
      startDate: '', // Clear date filters
      endDate: '',   // Clear date filters  
      pageNumber: 1,
    }));
  };

  const handlePageChange = (pageNumber: number) => {
    setQueryParams((prev) => ({ ...prev, pageNumber }));
  };

  const handlePageSizeChange = (pageSize: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize, pageNumber: 1 }));
  };

  const formatForZipFilename = (dateString: string) => format(new Date(dateString), "MM-dd-yyyy'T'HH:mm");

  const handleDownload = async (row: CCTVVideoRequest) => {
    try {
      const { data: videos } = await downloadVideosMutation.mutateAsync({
        lockerCode: row.lockerCode,
        startDate: new Date(row.startDate).toISOString(),
        endDate: new Date(row.endDate).toISOString(),
      });
      
      downloadZip(videos, `${row.lockerCode}-${formatForZipFilename(row.startDate)}-${formatForZipFilename(row.endDate)}`);
    } catch (err) {
      console.error('Download failed:', err);
    }
  };

  const getError = () => {
    const errorList = {
      getDataErrorMsg: t('get_data_error'),
      downloadVideoError: t('download_video_err'),
    };

    let currentError = '';
    if (error) {
      currentError = getErrorMessage(
        error as AxiosError,
        errorList.getDataErrorMsg
      );
    } else if (downloadVideosMutation.isError) {
      currentError = getErrorMessage(
        downloadVideosMutation.error as AxiosError,
        errorList.downloadVideoError
      );
    }

    return currentError;
  };

  return {
    // State
    queryParams,
    startDate,
    endDate,
    videoRequests: data?.data || [],
    totalCount: data?.totalCount || 0,
    isLoading: isLoading || downloadVideosMutation.isLoading,

    // State Setters
    setStartDate,
    setEndDate,
    setQuerySort,
    setQueryOrderBy,

    // Event Handlers
    handleSearch,
    refreshCctvVideoRequestsTable,
    handlePageChange,
    handlePageSizeChange,
    handleDownload,

    // Derived State
    error: getError(),
  };
};
